import { describeHandler } from "../inspector.js";
import { composeHandlers } from "./composed.js";
import { createDataHandler } from "./metadata.js";
import { focusHandler } from "./focus.js";
import { parentHandler } from "./handler.js";
import { traverseNodes } from "../tree/tree.js";
//#region src/handler/spatial.ts
/**
* Stores layout bounds per node (query key `spatialItem`).
*
* @see {@link https://fiveway.dev/guide/built-in-handlers#spatial-handler}
* @see {@link DataHandler}
*/
const spatialItemHandler = createDataHandler("spatialItem");
/**
* Spatial movement handler used by `spatialHandler`.
*
* Combines spatial movement with defaults for arrow-key style navigation using rects.
*
* @see {@link https://fiveway.dev/guide/built-in-handlers#spatial-handler}
* @see {@link NavigationHandler}
*/
const spatialMovementHandler = (action, { node, next }) => {
	if (import.meta.env.FIVEWAY_INSPECTOR ?? import.meta.env.DEV) describeHandler(action, { name: "spatial" });
	if (action.kind !== "move") return next();
	const direction = action.direction;
	if (direction === "forwards" || direction === "backwards" || direction === "back") return next();
	const focusedPos = spatialItemHandler.query(node.tree, node.tree.focus);
	if (focusedPos == null) return next();
	let closestId = null;
	let shortestDistance = null;
	traverseNodes(node.tree, node.id, 1, (id) => {
		const pos = spatialItemHandler.query(node.tree, id);
		if (pos === null) return;
		const distance = distanceSquared(focusedPos, pos, direction);
		if (distance !== null && (shortestDistance === null || distance < shortestDistance) && next(id, {
			kind: "focus",
			direction: null
		}) !== null) {
			closestId = id;
			shortestDistance = distance;
		}
	});
	return closestId ?? next();
};
/**
* Combines spatial movement with defaults for arrow-key style navigation using rects.
*
* @see {@link https://fiveway.dev/guide/built-in-handlers#spatial-handler}
* @see {@link ComposedHandler}
*/
const spatialHandler = composeHandlers([
	focusHandler({ focusWhenEmpty: false }),
	spatialMovementHandler,
	parentHandler
]);
/** Unit vectors from candidate toward focused, per move direction. */
const directionVector = {
	left: [1, 0],
	right: [-1, 0],
	up: [0, 1],
	down: [0, -1]
};
function distanceSquared(a, b, direction) {
	const dx = a.x - b.x;
	const dy = a.y - b.y;
	const [sx, sy] = directionVector[direction];
	if (dx * sx + dy * sy <= 0) return null;
	return dx * dx + dy * dy;
}
//#endregion
export { spatialHandler, spatialItemHandler, spatialMovementHandler };

//# sourceMappingURL=spatial.js.map